import type { ConversationStatus } from '@conversia/shared-types';

/**
 * Control manual del bot por conversación — lógica PURA y testeable que la ficha
 * cablea al endpoint de estado existente (`PATCH /api/conversations/:id/status`).
 *
 * El "control del bot" no es un flag aparte: se monta sobre el `ConversationStatus`
 * que ya gobierna si el agente responde. "Bot activo" ↔ "Atención humana" mapean a
 * ACTIVE ↔ WAITING_HUMAN. Así reusa el mecanismo (y el handler) existentes en vez
 * de añadir una columna nueva.
 */

export type BotControlMode = 'bot' | 'human';

/**
 * Estados de conversación en los que el bot (agente AI) responde automáticamente
 * los mensajes entrantes. ESPEJO de `RESPONDABLE_STATUSES` en
 * `apps/api/src/ai/ai-engine.ts`: si ambos divergen, el toggle mentiría sobre lo
 * que hace el handler.
 */
export const BOT_ACTIVE_STATUSES: readonly ConversationStatus[] = ['NEW', 'ACTIVE'];

/** ¿El bot está activo en esta conversación (responderá los mensajes entrantes)? */
export function isBotActive(status: ConversationStatus | null | undefined): boolean {
  return status != null && BOT_ACTIVE_STATUSES.includes(status);
}

/**
 * Estado de conversación destino al fijar el control manual:
 *  - `'bot'`   → ACTIVE: el agente retoma la conversación.
 *  - `'human'` → WAITING_HUMAN: atención humana; el agente NO responde aunque
 *    entren mensajes (lo respeta el handler de entrada vía `RESPONDABLE_STATUSES`).
 */
export function botControlTarget(mode: BotControlMode): ConversationStatus {
  return mode === 'bot' ? 'ACTIVE' : 'WAITING_HUMAN';
}
